import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { usePermission } from '../../hooks/usePermission';
import { ScriptLibrary, type ScriptTabDef } from './ScriptLibrary';

const { Text } = Typography;
const { TextArea } = Input;

type ScriptTabWithId = ScriptTabDef & { id?: string };

function stripIdsForLibrary(tabs: ScriptTabWithId[]): ScriptTabDef[] {
  return tabs.map(({ id: _omit, ...t }) => t);
}

export function RetainIQScriptsSection({
  scriptType,
  title,
}: {
  scriptType: 'outreach' | 'intervention';
  title: string;
}) {
  const queryClient = useQueryClient();
  const canManage = usePermission('retain_iq_reports');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ScriptTabWithId | null>(null);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery(
    ['retain-iq-scripts', scriptType],
    async () => {
      const res = await api.get('/api/retain-iq/scripts', { params: { type: scriptType } });
      return res.data as { tabs: ScriptTabWithId[]; fromDatabase: boolean };
    },
    { staleTime: 60_000 }
  );

  const tabs = data?.tabs || [];
  const fromDatabase = Boolean(data?.fromDatabase);
  const libraryTabs = useMemo(() => stripIdsForLibrary(tabs), [tabs]);

  const tabsWithIds = useMemo(() => tabs.filter((t): t is ScriptTabWithId & { id: string } => Boolean(t.id)), [tabs]);

  const openAdd = () => {
    setEditing(null);
    form.setFieldsValue({
      tab_key: '',
      tab_label: '',
      alert_type: undefined,
      alert_message: '',
      blocks: [{ phase: '', title: '', note: '', lines: [{ speaker: '', text: '' }] }],
    });
    setModalOpen(true);
  };

  const openEdit = (tab: ScriptTabWithId) => {
    setEditing(tab);
    form.setFieldsValue({
      tab_key: tab.key,
      tab_label: tab.label,
      alert_type: tab.alert?.type,
      alert_message: tab.alert?.message || '',
      blocks:
        tab.blocks && tab.blocks.length
          ? tab.blocks.map((b) => ({
              phase: b.phase,
              title: b.title,
              note: b.note || '',
              lines: b.lines && b.lines.length ? b.lines : [{ speaker: '', text: '' }],
            }))
          : [{ phase: '', title: '', note: '', lines: [{ speaker: '', text: '' }] }],
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submitModal = async () => {
    try {
      const v = await form.validateFields();
      const alert =
        v.alert_type && String(v.alert_message || '').trim()
          ? { type: v.alert_type, message: String(v.alert_message).trim() }
          : null;
      type MappedBlock = {
        phase: string;
        title: string;
        note?: string;
        lines: { speaker: string; text: string }[];
      };
      const blocks = (v.blocks || [])
        .map(
          (b: {
            phase: string;
            title: string;
            note?: string;
            lines: { speaker: string; text: string }[];
          }): MappedBlock => ({
            phase: String(b.phase || '').trim(),
            title: String(b.title || '').trim(),
            note: b.note && String(b.note).trim() ? String(b.note).trim() : undefined,
            lines: (b.lines || [])
              .map((ln: { speaker: string; text: string }) => ({
                speaker: String(ln.speaker || '').trim(),
                text: String(ln.text || '').trim(),
              }))
              .filter((ln) => ln.speaker || ln.text),
          })
        )
        .filter((b: MappedBlock) => b.phase || b.title || (b.lines && b.lines.length > 0));

      if (!blocks.length) {
        message.error('Add at least one script block with a phase or title and at least one line.');
        return;
      }

      if (editing?.id) {
        await api.put(`/api/retain-iq/scripts/${editing.id}`, {
          tab_key: String(v.tab_key).trim(),
          tab_label: String(v.tab_label).trim(),
          alert,
          blocks,
        });
        message.success('Script tab updated');
      } else {
        await api.post('/api/retain-iq/scripts', {
          script_type: scriptType,
          tab_key: String(v.tab_key).trim(),
          tab_label: String(v.tab_label).trim(),
          alert: alert || undefined,
          blocks,
        });
        message.success('Script tab created');
      }
      closeModal();
      queryClient.invalidateQueries(['retain-iq-scripts', scriptType]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      if (err.response?.data?.error) message.error(err.response.data.error);
    }
  };

  const loadStandardLibrary = async () => {
    try {
      await api.post('/api/retain-iq/scripts/reset-defaults', { script_type: scriptType });
      message.success('Standard library loaded into the database');
      queryClient.invalidateQueries(['retain-iq-scripts', scriptType]);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      message.error(e.response?.data?.error || 'Request failed');
    }
  };

  const restoreFactoryDefaults = async () => {
    try {
      await api.post('/api/retain-iq/scripts/reset-defaults', { script_type: scriptType });
      message.success('Restored factory default scripts');
      queryClient.invalidateQueries(['retain-iq-scripts', scriptType]);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      message.error(e.response?.data?.error || 'Request failed');
    }
  };

  const removeTab = async (id: string) => {
    await api.delete(`/api/retain-iq/scripts/${id}`);
    message.success('Script tab deleted');
    queryClient.invalidateQueries(['retain-iq-scripts', scriptType]);
  };

  const moveTab = useCallback(
    async (id: string, dir: number) => {
      const ids = tabsWithIds.map((t) => t.id);
      const index = ids.indexOf(id);
      const j = index + dir;
      if (index < 0 || j < 0 || j >= ids.length) return;
      const next = [...ids];
      [next[index], next[j]] = [next[j], next[index]];
      await api.put('/api/retain-iq/scripts/reorder', { script_type: scriptType, tab_ids_in_order: next });
      queryClient.invalidateQueries(['retain-iq-scripts', scriptType]);
    },
    [tabsWithIds, scriptType, queryClient]
  );

  const adminColumns = [
      {
        title: 'Order',
        key: 'ord',
        width: 100,
        render: (_: unknown, row: ScriptTabWithId & { id: string }) => {
          const idx = tabsWithIds.findIndex((t) => t.id === row.id);
          return (
            <Space>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={idx <= 0}
                onClick={() => void moveTab(row.id, -1)}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={idx < 0 || idx >= tabsWithIds.length - 1}
                onClick={() => void moveTab(row.id, 1)}
              />
            </Space>
          );
        },
      },
      { title: 'Key', dataIndex: 'key', width: 120 },
      { title: 'Label', dataIndex: 'label', ellipsis: true },
      {
        title: '',
        key: 'actions',
        width: 140,
        render: (_: unknown, row: ScriptTabWithId & { id: string }) => (
          <Space>
            <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
            <Popconfirm title="Delete this script tab?" onConfirm={() => void removeTab(row.id)}>
              <Button size="small" type="link" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

  return (
    <div>
      {canManage && (
        <Card size="small" style={{ marginBottom: 16 }} title="Admin — script library">
          {!fromDatabase ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Read-only bundled scripts"
                description="Everyone with RetainIQ access sees the standard library below. Load it into the database to enable add, edit, delete, and reorder for this script type."
              />
              <Button type="primary" icon={<DatabaseOutlined />} onClick={() => void loadStandardLibrary()}>
                Load standard library into database
              </Button>
            </Space>
          ) : (
            <Space wrap>
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                Add script tab
              </Button>
              <Popconfirm
                title="Replace all tabs with the factory default library?"
                description="This removes your custom scripts for this type."
                onConfirm={() => void restoreFactoryDefaults()}
                okText="Restore defaults"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<UndoOutlined />}>
                  Restore factory defaults
                </Button>
              </Popconfirm>
            </Space>
          )}
          {fromDatabase && tabsWithIds.length > 0 && (
            <Table<ScriptTabWithId & { id: string }>
              style={{ marginTop: 16 }}
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={tabsWithIds}
              columns={adminColumns}
            />
          )}
        </Card>
      )}

      <Spin spinning={isLoading}>
        {libraryTabs.length > 0 ? (
          <ScriptLibrary tabs={libraryTabs} title={title} />
        ) : (
          <Text type="secondary">No scripts to display.</Text>
        )}
      </Spin>

      <Modal
        title={editing?.id ? 'Edit script tab' : 'Add script tab'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitModal()}
        width={760}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="tab_key" label="Tab key" rules={[{ required: true, message: 'Unique id (e.g. ob30)' }]}>
            <Input placeholder="letters, numbers, underscore, hyphen" disabled={Boolean(editing?.id)} />
          </Form.Item>
          <Form.Item name="tab_label" label="Tab title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Optional banner alert">
            <Space align="start" wrap style={{ width: '100%' }}>
              <Form.Item name="alert_type" style={{ minWidth: 140 }}>
                <Select
                  allowClear
                  placeholder="Alert type"
                  options={[
                    { value: 'info', label: 'Info' },
                    { value: 'warning', label: 'Warning' },
                    { value: 'error', label: 'Error' },
                    { value: 'success', label: 'Success' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="alert_message" style={{ flex: 1, minWidth: 200 }}>
                <TextArea rows={2} placeholder="Banner message (shown with alert type)" />
              </Form.Item>
            </Space>
          </Form.Item>

          <Typography.Title level={5}>Script blocks</Typography.Title>
          <Form.List name="blocks">
            {(blockFields, { add: addBlock, remove: removeBlock }) => (
              <>
                {blockFields.map((bf) => (
                  <Card key={bf.key} size="small" style={{ marginBottom: 12 }} title={`Block ${bf.name + 1}`}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form.Item
                        {...bf}
                        name={[bf.name, 'phase']}
                        label="Phase"
                        rules={[{ required: true, message: 'Phase label (e.g. OPEN)' }]}
                      >
                        <Input placeholder="OPEN" />
                      </Form.Item>
                      <Form.Item {...bf} name={[bf.name, 'title']} label="Title" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                      <Form.Item {...bf} name={[bf.name, 'note']} label="Note (optional)">
                        <TextArea rows={2} />
                      </Form.Item>
                      <Typography.Text type="secondary">Lines</Typography.Text>
                      <Form.List name={[bf.name, 'lines']}>
                        {(lineFields, { add: addLine, remove: removeLine }) => (
                          <>
                            {lineFields.map((lf) => (
                              <Card key={lf.key} size="small" type="inner" style={{ marginBottom: 8 }}>
                                <Form.Item
                                  {...lf}
                                  name={[lf.name, 'speaker']}
                                  label="Speaker"
                                  rules={[{ required: true }]}
                                >
                                  <Input />
                                </Form.Item>
                                <Form.Item {...lf} name={[lf.name, 'text']} label="Script" rules={[{ required: true }]}>
                                  <TextArea rows={3} />
                                </Form.Item>
                                <Button type="link" danger size="small" onClick={() => removeLine(lf.name)}>
                                  Remove line
                                </Button>
                              </Card>
                            ))}
                            <Button type="dashed" size="small" onClick={() => addLine({ speaker: '', text: '' })}>
                              Add line
                            </Button>
                          </>
                        )}
                      </Form.List>
                      <Button type="link" danger size="small" onClick={() => removeBlock(bf.name)}>
                        Remove entire block
                      </Button>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    addBlock({ phase: '', title: '', note: '', lines: [{ speaker: '', text: '' }] })
                  }
                  block
                >
                  Add script block
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
}
